import { lighten } from '@mui/material';

import { typedStyles } from '@/utils/styleUtils';

import { colors } from '@/themesConfig/colors';

export const styles = typedStyles({
  modal: {
    display: 'flex',
    width: 'min(792px, 92%)',
    padding: '40px 40px 32px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '40px',
    borderRadius: '24px',
    boxSizing: 'border-box',
    overflow: 'visible',
  },

  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 1,
    width: '100%',
    pr: 5,
  },

  title: {
    fontWeight: 700,
    fontSize: 24,
    lineHeight: '32px',
    color: colors.brandBlack,
  },

  description: {
    color: colors.darkerGrey,
    fontFamily: 'Metropolis, sans-serif',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
  },

  body: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 3,
    width: '100%',
  },

  stationSelect: {
    position: 'relative',
    width: '100%',
    zIndex: 2,
  },

  stationTrigger: {
    width: '100%',
    cursor: 'pointer',
    borderBottom: `1px solid ${colors.mediumGrey}`,
    pb: 1,
  },

  /** Read-only site (opened from a station's Products tab): nothing to click. */
  stationTriggerLocked: {
    cursor: 'default',
  },

  stationLabel: {
    color: colors.darkGrey,
    fontSize: 12,
    lineHeight: '16px',
    mb: 1,
  },

  stationValueRow: {
    width: '100%',
    minHeight: 24,
  },

  stationValue: {
    flex: 1,
    minWidth: 0,
    color: colors.brandBlack,
    fontSize: 16,
    lineHeight: '24px',
  },

  /** Spinner slot while the preselected site loads: takes the name's place so the chevron stays right. */
  stationValueSpinner: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    minHeight: 24,
  },

  stationSpinner: {
    color: 'text.secondary',
  },

  stationChevron: {
    color: colors.darkGrey,
    fontSize: 20,
    flexShrink: 0,
    transition: 'transform 0.15s ease',
  },

  stationDropdown: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    left: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 360,
    borderRadius: 4,
    overflow: 'hidden',
    bgcolor: colors.white,
    boxShadow: 3,
    zIndex: 3,
  },

  stationSearch: {
    flexShrink: 0,
    p: 3,
    borderBottom: `1px solid ${colors.mediumGrey}`,
  },

  stationSearchIcon: {
    mr: 2,
    color: 'text.secondary',
    fontSize: 24,
  },

  stationOptions: {
    position: 'relative',
    flex: 1,
    minHeight: 120,
    maxHeight: 320,
    overflowY: 'auto',
    py: 0.5,
  },

  stationOption: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    px: 3,
    py: 1.25,
    cursor: 'pointer',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: colors.lighterGrey,
    },
  },

  stationOptionSelected: {
    backgroundColor: colors.lightGrey,
  },

  stationOptionTitle: {
    color: colors.brandBlack,
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 500,
  },

  stationOptionDetails: {
    color: colors.darkGrey,
    fontSize: 12,
    lineHeight: '16px',
  },

  dropZone: {
    position: 'relative',
    display: 'flex',
    height: 120,
    padding: '48px 24px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
    alignSelf: 'stretch',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: colors.white,
    boxSizing: 'border-box',
    backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
      `<svg xmlns='http://www.w3.org/2000/svg' width='100%' height='100%' fill='none'><rect x='0.75' y='0.75' width='99%' height='99%' rx='4' ry='4' stroke='#ABB2C2' stroke-width='1.25' stroke-dasharray='5 5'/></svg>`,
    )}")`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% 100%',
  },

  dropZoneOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: lighten(colors.brandBlue, 0.9),
    borderRadius: 'inherit',
    backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
      `<svg xmlns='http://www.w3.org/2000/svg' width='100%' height='100%' fill='none'><rect x='0.75' y='0.75' width='99%' height='99%' rx='4' ry='4' stroke='#4CC3FF' stroke-width='1.25' stroke-dasharray='5 5'/></svg>`,
    )}")`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% 100%',
  },

  dropZoneText: {
    textAlign: 'center',
    color: colors.darkGrey,
    fontFamily: 'Metropolis, Helvetica, Arial, sans-serif',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
  },

  browseButton: {
    display: 'inline',
    p: 0,
    m: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: '#1153C0',
    fontFamily: 'inherit',
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '24px',
    verticalAlign: 'baseline',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    '&:hover': {
      textDecoration: 'underline',
    },
  },

  fileChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1.25,
    alignSelf: 'flex-start',
    px: 1.5,
    py: 1,
    borderRadius: '24px',
    bgcolor: colors.lighterGrey,
    border: `1px solid ${colors.mediumGrey}`,
    maxWidth: '100%',
  },

  fileKind: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 34,
    height: 28,
    px: 0.75,
    borderRadius: '6px',
    bgcolor: colors.white,
    border: `1px solid ${colors.mediumGrey}`,
  },

  fileKindLabel: {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 0.3,
    lineHeight: 1,
    color: colors.brandBlack,
  },

  fileName: {
    flex: 1,
    minWidth: 0,
    color: colors.brandBlack,
  },

  fileRemove: {
    p: 0.25,
    color: colors.darkGrey,
  },

  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignSelf: 'stretch',
    gap: 2,
  },

  cancelButton: {
    minWidth: 112,
    height: 44,
    px: 3,
    borderRadius: '22px',
    borderWidth: 1,
    borderColor: colors.brandBlack,
    color: colors.brandBlack,
    fontWeight: 600,
    fontSize: 14,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      borderWidth: 1,
      borderColor: colors.brandBlack,
      backgroundColor: colors.lighterGrey,
    },
  },

  continueButton: {
    minWidth: 112,
    height: 44,
    px: 3,
    borderRadius: '22px',
    backgroundColor: colors.brandBlack,
    color: colors.white,
    fontWeight: 600,
    fontSize: 14,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: colors.almostBlack,
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: colors.lightGrey,
      color: colors.mediumGrey2,
    },
  },
});
